import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { MONTH_NAMES, calcSummary } from "./statusHelpers";

function formatLocalDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function getAllDatesInMonth(year, monthIndex) {
    const dates = [];
    const date = new Date(year, monthIndex, 1);
    while (date.getMonth() === monthIndex) {
        dates.push(formatLocalDate(date));
        date.setDate(date.getDate() + 1);
    }
    return dates;
}

function getDefaultStatusForDate(dateStr) {
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 ? "Week Off" : "Absent";
}

function resolveStatus(item) {
    // console.log("status", item);

    const hasPunchIn =
        item.punchIn &&
        item.punchIn !== "--" &&
        item.punchIn !== "NA" &&
        item.punchIn.trim() !== "";

    const hasPunchOut =
        item.punchOut &&
        item.punchOut !== "--" &&
        item.punchOut !== "NA" &&
        item.punchOut.trim() !== "";

    const penalty = (item.attStatus || item.penaltyReason).toLowerCase();
    // console.log(penalty, "p");
    if (
        penalty.includes("p/2") ||
        penalty.includes("half") ||
        penalty.includes("half-day") ||
        penalty.includes("early departure")
    ) {
        return "Half Day";
    }

    // Both punches present → Present (even on Sunday)
    if (hasPunchIn && hasPunchOut) return "Present";

    // Punch-in but no punch-out → Miss Punch
    if (hasPunchIn && !hasPunchOut) return "Miss Punch";

    // No punches at all → trust the API status, but guard Saturday
    const apiStatus = (item.attStatus ?? "Absent").trim();
    const date = new Date(item.date);
    const dayOfWeek = date.getDay();

    // Saturday (6): never show "Week Off" — show Absent instead
    if (dayOfWeek === 6 && apiStatus.toLowerCase().includes("week off")) {
        return "Absent";
    }

    return apiStatus || "Absent";
}

// function resolveStatus(item) {
//     const hasPunchIn =
//         item.punchIn &&
//         item.punchIn !== "--" &&
//         item.punchIn !== "NA";

//     const hasPunchOut =
//         item.punchOut &&
//         item.punchOut !== "--" &&
//         item.punchOut !== "NA";

//     const status = (item.attStatus || "").trim().toUpperCase();

//     // Trust backend first
//     switch (status) {
//         case "P":
//         case "PRESENT":
//             return "Present";

//         case "P/2":
//         case "HALF DAY":
//             return "Half Day";

//         case "A":
//         case "ABSENT":
//             return "Absent";

//         case "WO":
//         case "WEEK OFF":
//             return "Week Off";

//         case "L":
//         case "LEAVE":
//             return "Leave";
//     }

//     // Fallback only if backend didn't send attStatus

//     if (hasPunchIn && hasPunchOut)
//         return "Present";

//     if (hasPunchIn && !hasPunchOut)
//         return "Miss Punch";

//     return "Absent";
// }

export function useAttendanceData() {
    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    );
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Parse selectedMonth into year/month
    const currentDate = useMemo(() => {
        const [year, month] = selectedMonth.split("-");
        return new Date(parseInt(year), parseInt(month) - 1, 1);
    }, [selectedMonth]);

    const monthName = MONTH_NAMES[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    const monthIndex = currentDate.getMonth();

    const isCurrentMonth = useMemo(() => {
        const today = new Date();
        return year === today.getFullYear() && monthIndex === today.getMonth();
    }, [year, monthIndex]);

    const fetchData = useCallback(
        async (isRefresh = false) => {
            isRefresh ? setRefreshing(true) : setLoading(true);
            setError(null);

            try {
                // API date range (first and last day of selected month)
                const startDate = `${year}-${String(monthIndex + 1).padStart(2, "0")}-01`;
                const lastDay = new Date(year, monthIndex + 1, 0).getDate();
                const endDate = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${lastDay}`;

                // Fetch attendance from backend
                const res = await axios.get(
                    `${process.env.REACT_APP_SERVER_ADDRESS}/attendance/view-attendance`,
                    { params: { startDate, endDate } },
                );

                console.log("attendance data", res.data);

                const apiData = Array.isArray(res?.data?.data)
                    ? res.data.data.filter(Boolean)
                    : [];
                const todayStr = formatLocalDate(new Date());

                // Map backend records by date (YYYY-MM-DD)
                const apiRecordsMap = new Map();
                apiData.forEach((item) => {
                    const record = {
                        id: String(
                            item.id ?? item.attId ?? item._id ?? Math.random(),
                        ),
                        date: item.date, // must be YYYY-MM-DD
                        status: resolveStatus(item), // ← punch-first resolution

                        inTime: item.punchIn ?? "--",
                        outTime: item.punchOut ?? "--",
                        hours: item.working_Hrs ?? "0h",
                        isToday: item.date === todayStr,
                        penaltyReason: item.penaltyReason ?? null,
                        isTravel: item.isTravel ?? false,
                        travelFlagged: item.travelFlagged ?? false,
                        travelFlagReason: item.travelFlagReason ?? null,
                    };
                    apiRecordsMap.set(item.date, record);
                });

                // 1. Generate every day of the month
                let allDates = getAllDatesInMonth(year, monthIndex);

                // 2. For current month, show only days up to today
                if (isCurrentMonth) {
                    const todayDate = new Date(todayStr);
                    allDates = allDates.filter(
                        (dateStr) => new Date(dateStr) <= todayDate,
                    );
                }

                // 3. Merge: use backend record if exists, else default
                let monthData = allDates.map((dateStr) => {
                    if (apiRecordsMap.has(dateStr)) {
                        return apiRecordsMap.get(dateStr);
                    }
                    // No API record for this date — use safe defaults
                    return {
                        id: dateStr,
                        date: dateStr,
                        status: getDefaultStatusForDate(dateStr),
                        inTime: "--",
                        outTime: "--",
                        hours: "0h",
                        isToday: dateStr === todayStr,
                        penaltyReason: null,
                        isTravel: false,
                        travelFlagged: false,
                        travelFlagReason: null,
                    };
                });

                // 4. Sorting: today first, then descending (most recent to oldest)
                monthData.sort((a, b) => {
                    if (a.isToday) return -1;
                    if (b.isToday) return 1;
                    return new Date(b.date) - new Date(a.date);
                });

                setRecords(monthData);
            } catch (err) {
                console.error("Attendance fetch error:", err);
                setError("Failed to load attendance. Please try again.");
            } finally {
                isRefresh ? setRefreshing(false) : setLoading(false);
            }
        },
        [year, monthIndex, isCurrentMonth],
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const goToPrev = () => {
        const d = new Date(currentDate);
        d.setMonth(d.getMonth() - 1);
        setSelectedMonth(
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        );
    };

    const goToNext = () => {
        if (isCurrentMonth) return;
        const d = new Date(currentDate);
        d.setMonth(d.getMonth() + 1);
        setSelectedMonth(
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        );
    };

    const summary = useMemo(() => calcSummary(records), [records]);

    return {
        records,
        summary,
        loading,
        refreshing,
        error,
        monthName,
        year,
        isCurrentMonth,
        goToPrev,
        goToNext,
        refetch: () => fetchData(true),
    };
}
